import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
// import jwt_decode from 'jsonwebtoken';

import { Stack, TextField, IconButton, InputAdornment, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { jwtDecode } from 'jwt-decode';

import { setCredentials } from '../../../slices/authSlice';
import { API_BASE_URL } from '../../../utils/env';

// components
import Iconify from '../../../components/iconify';
// ----------------------------------------------------------------------

export default function LoginForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.auth.userInfo);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userInfo) {
      navigate('/dashboard/app', { replace: true });
    }
  }, [navigate, userInfo]);

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/Account/login`, {
        email,
        password,
      });

      // Decode JWT token to get user information
      const decodedToken = jwtDecode(response.data.token);

      // Extract the 'id' from the decoded token
      const parishId = decodedToken.parishId;

      // console.log('Authentication successful', response.data.token);

      // Dispatch the action to store user data in Redux
      dispatch(setCredentials(response.data, parishId));

      // console.log(response.data);

      navigate('/dashboard/app/');

      setLoading(false);
    } catch (error) {
      setLoading(false);

      if (error.response) {
        // Handle validation errors
        if (error.response.status === 400) {
          toast.error('Validation error: Please check your details.');
          showError(error.message);
        }
        // Handle other server-side errors
        else {
          toast.error('An error occurred. Please try again later.');
        }
      } else if (error.request) {
        // Handle network errors (no response received)
        toast.error('Network error: Please check your internet connection.');
      } else {
        // Handle other types of errors
        toast.error('An error occurred. Please try again later.');
      }
    }
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => {
      setError(null);
    }, 6000); // Hide error message after 3 seconds
  };

  return (
    <form onSubmit={submitHandler}>
      <Stack spacing={3}>
        <TextField
          sx={{
            // change the focus color
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000080',
            },
            // the label color
            '& .MuiInputLabel-outlined.Mui-focused': {
              color: '#000080',
            },
          }}
          name="email"
          label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          error={error !== null}
          className={error !== null ? 'shaky' : ''}
        />

        <TextField
          sx={{
            // change the focus color
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000080',
            },
            // the label color
            '& .MuiInputLabel-outlined.Mui-focused': {
              color: '#000080',
            },
          }}
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          error={error !== null}
          className={error !== null ? 'shaky' : ''}
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, mt: 1.5 }}>
        <Typography
          variant="subtitle2"
          underline="hover"
          style={{ textDecoration: 'none', color: '#000080', fontWeight: 600, fontSize: '15px', cursor: 'pointer' }}
          onClick={() => navigate('/forgot-password')}
          // to="/forgot-password"
        >
          Forgot Password?
        </Typography>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        sx={{
          backgroundColor: '#000080',
          '&:hover': {
            backgroundColor: '#000090',
          },
        }}
        loading={isLoading}
        disabled={isLoading}
      >
        Login
      </LoadingButton>
    </form>
  );
}
